/**
 * DocumentPickerContent — pure UI document picker.
 *
 * Knows nothing about where documents come from. Caller provides rows,
 * filter definitions, filter state, pagination, and selection state.
 * This component renders a compact filter bar + TanStack Table.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookIcon, FileTextIcon, Icon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { ColumnDef, OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { Checkbox } from 'src/components/ui/Checkbox';
import Popover from '@/components/ui/Popover';
import TanStackTable from 'src/components/ui/TanStackTable';
import WikiFilterDropdown from 'src/features/wiki/components/WikiFilterDropdown';
import SearchInputField from 'src/components/ui/SearchInput';
import Button from 'src/components/ui/Button';
import { formatDateBritish } from 'src/lib/utils/formatDate';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface DocumentPickerRow {
  id: string;
  name: string;
  /** Raw filename stem (without extension). Shown below the title when present and different. */
  filename?: string;
  /** ISO datetime string for the "Date Modified" column (optional). */
  updatedAt?: string;
  /** Optional pill shown to the right of the name (e.g. "Hub", "Wiki"). */
  badge?: { label: string; className: string };
}

export interface DocumentPickerOption {
  value: string;
  label: string;
}

/** Describes one filter button in the toolbar. */
export interface DocumentPickerFilterDef {
  /** Key used in `DocumentPickerFilters.active` to store selected values. */
  key: string;
  label: string;
  icon?: Icon;
  /** Flat option list — used when `sections` is absent. */
  options: DocumentPickerOption[];
  searchable?: boolean;
  placeholder?: string;
  /** When set, the popover renders labelled sections instead of a flat list. */
  sections?: Array<{
    label: string;
    options: DocumentPickerOption[];
  }>;
}

/** All filter state — own this in the parent and pass it down. */
export interface DocumentPickerFilters {
  search: string;
  /** Selected values per filter key (matches `DocumentPickerFilterDef.key`). */
  active: Record<string, string[]>;
}

export const EMPTY_DOCUMENT_PICKER_FILTERS: DocumentPickerFilters = {
  search: '',
  active: {}
};

export interface DocumentPickerContentProps {
  // ── Data (provided by caller) ─────────────────────────────────────────────
  rows: DocumentPickerRow[];
  isLoading?: boolean;

  // ── Pagination (optional — omit for unpaginated lists) ────────────────────
  page?: number;
  pageCount?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;

  // ── Selection (controlled) ────────────────────────────────────────────────
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;

  // ── Filters (fully controlled by caller) ─────────────────────────────────
  filters: DocumentPickerFilters;
  onFiltersChange: (next: DocumentPickerFilters) => void;

  // ── Filter definitions (which buttons to show) ────────────────────────────
  filterDefs?: DocumentPickerFilterDef[];

  /** When true, selection is disabled (rows render but clicks are no-ops). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function usePickerColumns(
  rows: DocumentPickerRow[],
  disabled: boolean,
  hoveredRowId: string | null
): ColumnDef<DocumentPickerRow, any>[] {
  const hasBadges = useMemo(() => rows.some((r) => !!r.badge), [rows]);
  const hasDates = useMemo(() => rows.some((r) => !!r.updatedAt), [rows]);

  return useMemo(
    () => [
      // ── Select column ────────────────────────────────────────────────────
      {
        id: 'select',
        size: 48,
        minSize: 48,
        maxSize: 48,
        enableSorting: false,
        enableResizing: false,
        header: ({ table }) => (
          <div className="flex-shrink-0 flex items-center justify-center">
            <Checkbox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.toggleAllRowsSelected(e.target.checked);
              }}
              disabled={disabled}
            />
          </div>
        ),
        cell: ({ row }) => {
          const isSelected = row.getIsSelected();
          const isHovered = hoveredRowId === row.id;
          if (isSelected || isHovered) {
            return (
              <div className="flex-shrink-0 flex items-center justify-center">
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    row.toggleSelected(e.target.checked);
                  }}
                  disabled={disabled}
                />
              </div>
            );
          }
          const isWiki = row.original.badge?.label !== 'Document Hub';
          const RowIcon = isWiki ? BookIcon : FileTextIcon;
          return (
            <div className="flex-shrink-0 flex items-center justify-center text-content-subtler">
              <RowIcon size={16} weight="light" />
            </div>
          );
        }
      },
      // ── Name column ──────────────────────────────────────────────────────
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => {
          const { name, filename, badge } = row.original;
          const isWiki = badge?.label !== 'Document Hub';
          const showFilename = !isWiki && filename && filename !== name;
          return (
            <div className="flex flex-col min-w-0">
              <span className={`truncate text-sm ${disabled ? 'text-content-subtle' : 'text-content-default'}`}>
                {name}
              </span>
              {showFilename && <span className="truncate text-xs text-content-subtlest">{filename}</span>}
            </div>
          );
        }
      },
      // ── Source column (only when rows carry badges) ───────────────────────
      ...(hasBadges
        ? [
            {
              id: 'source',
              header: 'Source',
              size: 150,
              minSize: 150,
              maxSize: 150,
              enableSorting: false,
              enableResizing: false,
              cell: ({ row }: { row: { original: DocumentPickerRow } }) =>
                row.original.badge ? (
                  <span
                    className={`inline-block max-w-full truncate text-[10px] font-medium px-1.5 py-0.5 rounded ${row.original.badge.className}`}
                    title={row.original.badge.label}
                  >
                    {row.original.badge.label}
                  </span>
                ) : null
            } as ColumnDef<DocumentPickerRow, any>
          ]
        : []),
      // ── Date Modified column (only when rows carry dates) ─────────────────
      ...(hasDates
        ? [
            {
              id: 'updatedAt',
              header: 'Modified',
              size: 110,
              minSize: 110,
              maxSize: 110,
              enableSorting: false,
              enableResizing: false,
              cell: ({ row }: { row: { original: DocumentPickerRow } }) =>
                row.original.updatedAt ? (
                  <span className="text-xs text-content-subtle whitespace-nowrap">
                    {formatDateBritish(row.original.updatedAt)}
                  </span>
                ) : null
            } as ColumnDef<DocumentPickerRow, any>
          ]
        : [])
    ],
    [hasBadges, hasDates, disabled, hoveredRowId]
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DocumentPickerContent({
  rows,
  isLoading = false,
  page,
  pageCount,
  totalCount,
  onPageChange,
  selectedIds,
  onSelectionChange,
  filters,
  onFiltersChange,
  filterDefs = [],
  disabled = false
}: DocumentPickerContentProps) {
  // ── Transient UI state ────────────────────────────────────────────────────
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  useEffect(() => {
    if (searchExpanded) searchInputRef.current?.focus();
  }, [searchExpanded]);

  // ── TanStack Table — controlled selection ─────────────────────────────────
  const columns = usePickerColumns(rows, disabled, hoveredRowId);

  const rowSelection: RowSelectionState = useMemo(
    () => Object.fromEntries(selectedIds.map((id) => [id, true])),
    [selectedIds]
  );

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updater) => {
      if (disabled) return;
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      onSelectionChange(
        Object.entries(next)
          .filter(([, v]) => v)
          .map(([k]) => k)
      );
    },
    [disabled, rowSelection, onSelectionChange]
  );

  // ── Filter helpers ────────────────────────────────────────────────────────
  const hasAnyFilter = !!filters.search || Object.values(filters.active).some((v) => v.length > 0);

  const setSearch = (search: string) => onFiltersChange({ ...filters, search });

  const setFilterActive = (key: string, values: string[]) =>
    onFiltersChange({ ...filters, active: { ...filters.active, [key]: values } });

  const clearAll = () => {
    onFiltersChange(EMPTY_DOCUMENT_PICKER_FILTERS);
    setSearchExpanded(false);
  };

  const openPopover = (el: HTMLElement, key: string) => {
    setAnchorEl(el);
    setOpenFilterKey(key);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setOpenFilterKey(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-2 overflow-hidden h-full w-full">
      {/* ── Filter bar ── */}
      <div className="flex items-center gap-1 shrink-0 justify-end flex-wrap">
        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearAll}
            className="text-sm text-misc-link underline underline-offset-2 hover:opacity-75 transition-opacity whitespace-nowrap mr-2"
          >
            Clear filters
          </button>
        )}

        {searchExpanded ? (
          <SearchInputField
            ref={searchInputRef}
            autoFocus
            value={filters.search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            className="w-44 h-[28px] text-xs"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearch('');
                setSearchExpanded(false);
              }
            }}
            onBlur={(e) => {
              if (!e.target.value) setSearchExpanded(false);
            }}
          />
        ) : (
          <Button
            variant={filters.search ? 'primary' : 'ghost'}
            size="md"
            icon={MagnifyingGlassIcon}
            onClick={() => setSearchExpanded(true)}
          />
        )}

        {filterDefs.map((def) => {
          const count = filters.active[def.key]?.length ?? 0;
          const FilterIcon = def.icon;
          return (
            <Button
              key={def.key}
              variant={count > 0 ? 'primary' : 'ghost'}
              size="md"
              icon={FilterIcon}
              onClick={(e) => openPopover(e.currentTarget, def.key)}
            >
              {def.label}
              {count > 0 ? ` (${count})` : ''}
            </Button>
          );
        })}
      </div>

      {/* ── Filter popovers ── */}
      <Popover
        open={!!openFilterKey}
        anchorEl={anchorEl}
        onClose={closePopover}
        placement="bottom-start"
        className="mt-1 overflow-hidden min-w-[220px]"
      >
        {filterDefs
          .filter((d) => d.key === openFilterKey)
          .map((def) => {
            const active = filters.active[def.key] ?? [];
            const toggle = (val: string) =>
              setFilterActive(def.key, active.includes(val) ? active.filter((v) => v !== val) : [...active, val]);

            if (def.sections) {
              return (
                <div key={def.key} className="py-2 max-h-80 overflow-y-auto custom-scrollbar">
                  {def.sections.map((section, i) => (
                    <div key={section.label}>
                      {i > 0 && <div className="my-1 border-t border-border-default" />}
                      <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-subtler">
                        {section.label}
                      </p>
                      {section.options.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggle(opt.value)}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-left text-content-default hover:bg-surface-hover transition-colors"
                        >
                          <Checkbox
                            checked={active.includes(opt.value)}
                            readOnly
                            tabIndex={-1}
                            aria-hidden="true"
                            className="pointer-events-none"
                          />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              );
            }

            return (
              <WikiFilterDropdown
                key={def.key}
                bare
                options={def.options}
                value={active}
                onChange={(v) => {
                  setFilterActive(def.key, v);
                  closePopover();
                }}
                label={def.label}
                placeholder={def.placeholder}
                searchable={def.searchable ?? false}
                initialOpen
              />
            );
          })}
      </Popover>

      {/* ── Document table ── */}
      <div className="flex-1 min-h-0 min-w-0 overflow-auto">
        <TanStackTable<DocumentPickerRow>
          data={rows}
          columns={columns}
          getRowId={(row) => row.id}
          multiselect
          rowSelection={rowSelection}
          onRowSelectionChange={disabled ? undefined : handleRowSelectionChange}
          onRowClick={
            disabled
              ? undefined
              : (row) => {
                  const next = { ...rowSelection };
                  if (next[row.id]) delete next[row.id];
                  else next[row.id] = true;
                  onSelectionChange(Object.keys(next).filter((k) => next[k]));
                }
          }
          onHoveredRowChange={(id) => setHoveredRowId(id != null ? String(id) : null)}
          enableSorting={false}
          enableResizing={false}
          fluidColumnId="name"
          emptyMessage={isLoading ? 'Loading…' : 'No documents found'}
          {...(onPageChange && pageCount != null
            ? { currentPage: page, pageCount, totalItems: totalCount, onPageChange }
            : { itemsPerPage: rows.length || 1 })}
        />
      </div>
    </div>
  );
}
